import { useState, useMemo } from 'react'
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom'
import { User, Package, Heart, MapPin, Settings, LogOut, ChevronRight, Trash2, ShoppingCart, FileText, Copy, RotateCcw, Search, Star, TrendingUp, Gift, Bell } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { orderService, productService, templateService, promotionService, publicSettingsService } from '../services/api'
import { PageSpinner } from '../components/ui'
import toast from 'react-hot-toast'
import { useWishlistStore } from '../store/wishlistStore'
import { useCartStore } from '../store/cartStore'

function getCategoryEmoji(item) {
  const cat      = (item?.category_name || '').toLowerCase()
  const combined = `${item?.description || ''} ${item?.brand || ''}`.toLowerCase()

  // Category name first
  if (/whiskey|whisky|bourbon|scotch/.test(cat))     return '🥃'
  if (/vodka/.test(cat))                              return '🍸'
  if (/\bgin\b/.test(cat))                            return '🌿'
  if (/tequila/.test(cat))                            return '🌵'
  if (/rum/.test(cat))                                return '🏝️'
  if (/brandy|cognac/.test(cat))                      return '🍂'
  if (/liqueur/.test(cat))                            return '✨'
  if (/aperitif/.test(cat))                           return '🫗'
  if (/\bcane\b/.test(cat))                           return '🌾'
  if (/shooter/.test(cat))                            return '🔫'
  if (/champagne|sparkling/.test(cat))                return '🥂'
  if (/port|sherry|muscadel/.test(cat))               return '🫙'
  if (/box wine/.test(cat))                           return '📦'
  if (/wine/.test(cat))                               return '🍷'
  if (/craft beer/.test(cat))                         return '🍻'
  if (/cider/.test(cat))                              return '🍋'
  if (/rtd cocktail/.test(cat))                       return '🍹'
  if (/rtd/.test(cat))                                return '🥤'
  if (/beer/.test(cat))                               return '🍺'
  if (/energy/.test(cat))                             return '⚡'
  if (/juice/.test(cat))                              return '🍊'
  if (/cordial/.test(cat))                            return '🍓'
  if (/water/.test(cat))                              return '🫧'
  if (/mineral/.test(cat))                            return '💧'
  if (/cigarette|tobacco/.test(cat))                  return '🚬'
  if (/spirits/.test(cat))                            return '🥃'
  if (/snack|chips|crisp|nut|biltong|droëwors|popcorn/.test(cat)) return '🍿'
  if (/glassware|glass/.test(cat))                    return '🥂'
  if (/gift|hamper|accessor/.test(cat))               return '🎁'

  // SA snack brands before alcohol keyword fallback
  if (/willards|simba|lays|pringles|doritos|niknaks|nik nak|cheesenak|biltong|droewors|popcorn|pretzels|peanut|nachos/.test(combined)) return '🍿'

  // Keyword fallback
  if (/whiskey|whisky|bourbon|scotch/.test(combined)) return '🥃'
  if (/vodka/.test(combined))                          return '🍸'
  if (/\bgin\b/.test(combined))                        return '🌿'
  if (/tequila|mezcal/.test(combined))                 return '🌵'
  if (/\brum\b/.test(combined))                        return '🏝️'
  if (/brandy|cognac/.test(combined))                  return '🍂'
  if (/liqueur/.test(combined))                        return '✨'
  if (/champagne|sparkling|prosecco/.test(combined))   return '🥂'
  if (/port|sherry|muscadel/.test(combined))           return '🫙'
  if (/wine/.test(combined))                           return '🍷'
  if (/beer|lager|ale|stout/.test(combined))           return '🍺'
  if (/energy drink|monster|red bull/.test(combined))  return '⚡'
  if (/juice|nectar/.test(combined))                   return '🍊'
  if (/water/.test(combined))                          return '🫧'
  if (/cigarette|tobacco/.test(combined))              return '🚬'
  if (/glass|glassware/.test(combined))                return '🥂'
  if (/gift|hamper/.test(combined))                    return '🎁'
  if (/snack|nut|chip|crisp|popcorn|biltong/.test(combined)) return '🍿'

  return '🍾'
}

// ── Account Dashboard ─────────────────────────────────────────────
export function Account() {
  const { user, logout, isAuthenticated, loading } = useAuth()
  const { items: wishlistItems } = useWishlistStore()

  const { data: ordersData } = useQuery({
    queryKey: ['orders', 'mine'],
    queryFn: orderService.getMine,
    enabled: isAuthenticated,
  })

  const { data: templatesData } = useQuery({
    queryKey: ['templates', 'mine'],
    queryFn: templateService.getAll,
    enabled: isAuthenticated,
  })

  const { data: promosData } = useQuery({
    queryKey: ['promotions'],
    queryFn: promotionService.getMyPromos,
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000,
  })

  if (loading) return <PageSpinner />
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: { pathname: '/account' } }} replace />

  const orders = Array.isArray(ordersData) ? ordersData : (ordersData?.orders || ordersData?.items || [])
  const templates = Array.isArray(templatesData) ? templatesData : (templatesData?.items || [])
  const promos = (Array.isArray(promosData) ? promosData : []).filter(p => !p.dismissed)
  const unreadPromos = promos.filter(p => !p.read).length
  const totalSpent = orders.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0)
  const activeOrders = orders.filter(o => ['pending','processing','shipped'].includes(o.status)).length

  const menuItems = [
    { icon: Package, label: 'My Orders', to: '/orders', desc: 'Track & manage orders', badge: activeOrders || null },
    { icon: Heart, label: 'Wishlist', to: '/wishlist', desc: 'Saved products', badge: wishlistItems.length || null },
    { icon: Gift, label: 'Promotions', to: '/promotions', desc: 'Exclusive offers for you', badge: unreadPromos || null },
    { icon: MapPin, label: 'Addresses', to: '/account/addresses', desc: 'Delivery addresses' },
    { icon: FileText, label: 'Templates', to: '/templates', desc: 'Reusable order templates', badge: templates.length || null },
    { icon: Settings, label: 'Settings', to: '/account/settings', desc: 'Profile & password' },
  ]

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 animate-fade-in">
      {/* Profile Hero */}
      <div className="card p-6 mb-5">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-2xl font-heading text-white shadow-lg">
            {user?.username?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="font-heading text-xl text-gray-900 dark:text-white">{user?.username}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: Package, label: 'Orders', value: orders.length },
            { icon: TrendingUp, label: 'Spent', value: `R${totalSpent.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` },
            { icon: Heart, label: 'Saved', value: wishlistItems.length },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 text-center">
              <div className="font-heading text-lg text-primary dark:text-red-400">{value}</div>
              <div className="text-xs text-gray-400 font-display">{label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {menuItems.map(({ icon: Icon, label, to, desc, badge }) => (
          <Link key={to} to={to} className="card p-5 flex items-center gap-4 hover:border-primary/30 border border-transparent transition-colors group">
            <div className="relative w-12 h-12 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
              <Icon size={20} className="text-primary dark:text-red-400 group-hover:text-white" />
              {badge > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </div>
            <div className="flex-1">
              <div className="font-display font-semibold text-gray-900 dark:text-white">{label}</div>
              <div className="text-xs text-gray-400 mt-0.5">{desc}</div>
            </div>
            <ChevronRight size={16} className="text-gray-300 group-hover:text-primary transition-colors" />
          </Link>
        ))}
      </div>

      <button
        onClick={logout}
        className="mt-6 w-full flex items-center justify-center gap-2 py-3 text-red-500 hover:text-red-600 font-display font-medium text-sm transition-colors"
      >
        <LogOut size={16} />
        Sign Out
      </button>
    </div>
  )
}

// ── Orders ────────────────────────────────────────────────────────
const ORDER_STATUS_TABS = ['All', 'Active', 'Delivered', 'Cancelled']

function statusGroup(s) {
  if (['pending', 'processing', 'confirmed', 'shipped'].includes(s)) return 'Active'
  if (['delivered', 'completed'].includes(s)) return 'Delivered'
  if (s === 'cancelled') return 'Cancelled'
  return 'Active'
}

export function Orders() {
  const { isAuthenticated, loading } = useAuth()
  const [tab, setTab] = useState('All')
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['orders', 'mine'],
    queryFn: orderService.getMine,
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
  })

  const { data: pubSettings } = useQuery({
    queryKey: ['public-settings'],
    queryFn: () => publicSettingsService.get().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const showOrderStatus = pubSettings?.show_order_status ?? false

  if (loading) return <PageSpinner />
  if (!isAuthenticated) return <Navigate to="/login" replace />

  const allOrders = Array.isArray(data) ? data : (data?.orders || data?.items || [])

  const filtered = useMemo(() => {
    let list = allOrders
    if (tab !== 'All') list = list.filter(o => statusGroup(o.status) === tab)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(o =>
        String(o.id).includes(q) ||
        (o.notes || '').toLowerCase().includes(q) ||
        (o.status || '').includes(q)
      )
    }
    return list
  }, [allOrders, tab, search])

  const tabCounts = useMemo(() => ({
    All: allOrders.length,
    Active: allOrders.filter(o => statusGroup(o.status) === 'Active').length,
    Delivered: allOrders.filter(o => statusGroup(o.status) === 'Delivered').length,
    Cancelled: allOrders.filter(o => statusGroup(o.status) === 'Cancelled').length,
  }), [allOrders])

  const statusColor = (s) => ({
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    shipped: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    delivered: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }[s] || 'bg-gray-100 text-gray-700')

  const statusIcon = (s) => ({
    pending: '🕐', processing: '⚙️', confirmed: '✅', shipped: '🚚',
    delivered: '📦', completed: '🎉', cancelled: '❌',
  }[s] || '📋')

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4 mb-5">
        <Link to="/account" className="text-gray-400 hover:text-gray-600 transition-colors">
          <ChevronRight size={18} className="rotate-180" />
        </Link>
        <h1 className="section-title">My Orders</h1>
        {allOrders.length > 0 && (
          <span className="ml-auto text-xs text-gray-400 font-display">{allOrders.length} total</span>
        )}
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scroll-hide">
        {ORDER_STATUS_TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-display font-semibold transition-colors ${
              tab === t
                ? 'bg-primary text-white shadow-sm'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {t}
            {tabCounts[t] > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${tab === t ? 'bg-white/25 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'}`}>
                {tabCounts[t]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search bar */}
      {allOrders.length >= 3 && (
        <div className="relative mb-5">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order # or status…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input-field pl-9 py-2.5 text-sm w-full"
          />
        </div>
      )}

      {isLoading ? <PageSpinner /> : filtered.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-5xl mb-4">{tab === 'All' && !search ? '📦' : '🔍'}</div>
          <h3 className="font-heading text-xl mb-3">
            {tab === 'All' && !search ? 'No orders yet' : 'No matching orders'}
          </h3>
          {tab === 'All' && !search
            ? <Link to="/shop" className="btn-primary">Start Shopping</Link>
            : <button onClick={() => { setTab('All'); setSearch('') }} className="btn-outline">Clear filters</button>
          }
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(order => (
            <div key={order.id} className="card p-5 hover:border-primary/20 border border-transparent transition-colors">
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                <div className="flex items-start gap-3">
                  <div className="text-2xl mt-0.5 select-none">{showOrderStatus ? statusIcon(order.status) : '📦'}</div>
                  <div>
                    <div className="flex items-center gap-3 mb-0.5">
                      <span className="font-mono text-sm font-bold text-gray-800 dark:text-white">#{order.id}</span>
                      {showOrderStatus && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-display font-semibold ${statusColor(order.status)}`}>
                          {order.status || 'Pending'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">
                      {(order.order_date || order.created_at) ? new Date(order.order_date || order.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="text-right flex flex-col items-end gap-1">
                  <div className="font-display font-bold text-lg text-primary dark:text-red-400">
                    R {parseFloat(order.total_amount || 0).toFixed(2)}
                  </div>
                  <Link to={`/orders/${order.id}`} className="text-xs text-primary hover:underline">
                    View Details →
                  </Link>
                  <ReorderButton orderId={order.id} />
                  <SaveOrderAsTemplate orderId={order.id} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}



// ── Reorder Button ─────────────────────────────────────────────────
function ReorderButton({ orderId, fullWidth = false }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const mut = useMutation({
    mutationFn: () => orderService.reorder(orderId),
    onSuccess: (res) => {
      qc.invalidateQueries(['cart'])
      toast.success(`${res?.data?.added ?? 'Items'} item(s) added to cart`)
      navigate('/cart')
    },
    onError: () => toast.error('Failed to reorder'),
  })

  return (
    <button
      onClick={() => mut.mutate()}
      disabled={mut.isLoading}
      className={`flex items-center gap-1.5 text-xs font-display font-medium text-primary hover:text-primary-dark disabled:opacity-50 transition-colors mt-1 ${
        fullWidth ? 'w-full justify-center btn-outline py-2.5 text-sm' : ''
      }`}
      title="Add all items from this order to cart"
    >
      <RotateCcw size={fullWidth ? 16 : 11} />
      {mut.isLoading ? 'Adding…' : fullWidth ? 'Reorder — Add to Cart' : 'Reorder'}
    </button>
  )
}

// ── Save Order as Template ─────────────────────────────────────────
function SaveOrderAsTemplate({ orderId }) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')

  const mut = useMutation({
    mutationFn: () => templateService.fromOrder(orderId, { name: name.trim() }),
    onSuccess: () => {
      setOpen(false)
      setName('')
      import('react-hot-toast').then(m => m.default.success('Saved as template'))
    },
    onError: () => import('react-hot-toast').then(m => m.default.error('Failed to save')),
  })

  if (!open) return (
    <button
      onClick={() => setOpen(true)}
      className="text-xs text-gray-400 hover:text-primary flex items-center gap-1 mt-1 transition-colors"
    >
      <Copy size={11} /> Save as template
    </button>
  )

  return (
    <div className="mt-2 flex gap-1.5">
      <input
        className="border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1 text-xs flex-1 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:outline-none focus:border-primary"
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="Template name…"
        autoFocus
      />
      <button onClick={() => mut.mutate()} disabled={!name.trim() || mut.isLoading} className="text-xs px-2 py-1 bg-primary text-white rounded-lg disabled:opacity-50">
        {mut.isLoading ? '…' : 'Save'}
      </button>
      <button onClick={() => { setOpen(false); setName('') }} className="text-xs px-2 py-1 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-500">
        ×
      </button>
    </div>
  )
}

// ── Wishlist ──────────────────────────────────────────────────────
export function Wishlist() {
  const { items, removeItem, toggle, syncFromServer } = useWishlistStore()
  const addToCart = useCartStore(s => s.addItem)
  const [addedIds, setAddedIds] = useState({})

  // Re-sync on mount to always show freshest data
  useState(() => { syncFromServer() })

  const handleAddToCart = (item) => {
    const cartProduct = {
      id: item.product_id,
      description: item.description,
      brand: item.brand,
      primary_image: item.primary_image,
      inclprice2: item.inclprice2,
      stock_code: item.stock_code,
    }
    addToCart(cartProduct)
    setAddedIds(prev => ({ ...prev, [item.product_id]: true }))
    toast.success(`${item.description || 'Item'} added to cart`)
    setTimeout(() => setAddedIds(prev => { const n = { ...prev }; delete n[item.product_id]; return n }), 2000)
  }

  const handleRemove = async (item) => {
    await removeItem(item.id, item.product_id)
    toast.success('Removed from wishlist')
  }

  const handleAddAll = () => {
    if (!items.length) return
    items.forEach(item => {
      addToCart({
        id: item.product_id,
        description: item.description,
        brand: item.brand,
        primary_image: item.primary_image,
        inclprice2: item.inclprice2,
        stock_code: item.stock_code,
      })
    })
    toast.success(`${items.length} items added to cart`)
  }

  const totalValue = items.reduce((s, item) => s + parseFloat(item.inclprice2 || 0), 0)

  if (!items.length) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center animate-fade-in">
        <div className="text-6xl mb-4">💝</div>
        <h2 className="font-heading text-2xl mb-3">Your wishlist is empty</h2>
        <p className="text-gray-500 mb-6">Save products you love to find them easily.</p>
        <Link to="/shop" className="btn-primary">Browse Products</Link>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/account" className="text-gray-400 hover:text-gray-600 transition-colors">
            <ChevronRight size={18} className="rotate-180" />
          </Link>
          <div>
            <h1 className="section-title">Wishlist</h1>
            <p className="text-xs text-gray-400 font-display mt-0.5">
              {items.length} item{items.length !== 1 ? 's' : ''} · Total value: <span className="text-primary dark:text-red-400 font-semibold">R {totalValue.toFixed(2)}</span>
            </p>
          </div>
        </div>
        {items.length > 1 && (
          <button
            onClick={handleAddAll}
            className="btn-primary py-2 px-4 text-sm flex items-center gap-2"
          >
            <ShoppingCart size={14} /> Add All to Cart
          </button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(item => {
          const imgSrc = item.primary_image ? `/MappedData/${item.primary_image}` : null
          const price = parseFloat(item.inclprice2 || 0)
          const isAdded = addedIds[item.product_id]

          return (
            <div key={item.product_id} className="card p-4 group hover:border-primary/20 border border-transparent transition-colors">
              {/* Image */}
              <Link to={`/product/${item.product_id}`} className="block aspect-square bg-white rounded-xl mb-3 overflow-hidden relative">
                {imgSrc ? (
                  <img
                    src={imgSrc}
                    alt={item.description}
                    onError={e => { e.currentTarget.style.display='none'; e.currentTarget.nextSibling.style.display='flex' }}
                    className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-300"
                  />
                ) : null}
                <div
                  className="w-full h-full items-center justify-center text-5xl"
                  style={{ display: imgSrc ? 'none' : 'flex' }}
                >{getCategoryEmoji(item)}</div>
                {/* Remove overlay */}
                <button
                  onClick={e => { e.preventDefault(); handleRemove(item) }}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white dark:bg-gray-800 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 dark:hover:bg-red-900/20"
                  title="Remove from wishlist"
                >
                  <Trash2 size={12} className="text-red-400" />
                </button>
              </Link>

              {/* Info */}
              <Link to={`/product/${item.product_id}`} className="block">
                {item.brand && (
                  <p className="text-xs text-primary dark:text-red-400 font-display font-semibold mb-0.5 uppercase tracking-wide">{item.brand}</p>
                )}
                <h3 className="font-display font-semibold text-sm text-gray-900 dark:text-white line-clamp-2 hover:text-primary dark:hover:text-red-400 transition-colors">
                  {item.description}
                </h3>
              </Link>
              <p className="text-primary dark:text-red-400 font-bold mt-1 text-sm">
                R {price.toFixed(2)}
              </p>

              {/* Actions */}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => handleAddToCart(item)}
                  className={`flex-1 py-2 text-xs flex items-center justify-center gap-1 rounded-xl font-display font-semibold transition-all ${
                    isAdded ? 'bg-green-500 text-white' : 'btn-primary'
                  }`}
                >
                  <ShoppingCart size={13} />
                  {isAdded ? 'Added ✓' : 'Add to Cart'}
                </button>
                <button
                  onClick={() => handleRemove(item)}
                  className="p-2 rounded-xl border border-gray-200 dark:border-gray-600 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Contact ───────────────────────────────────────────────────────
export function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' })
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSending(true)
    setError('')
    try {
      await api.post('/contact', form)
      setSent(true)
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send message. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 animate-fade-in">
      <h1 className="section-title mb-2">Contact Us</h1>
      <p className="text-gray-500 dark:text-gray-400 mb-10">Get in touch for bulk orders, pricing, or any queries.</p>

      <div className="grid md:grid-cols-2 gap-10">
        <div className="card p-6">
          {sent ? (
            <div className="text-center py-8">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="font-heading text-xl mb-2">Message Sent!</h3>
              <p className="text-gray-500 text-sm">We'll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Name *</label>
                  <input className="input-field" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Phone</label>
                  <input className="input-field" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Email *</label>
                <input type="email" className="input-field" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Subject</label>
                <select className="input-field" value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}>
                  <option value="">Select a topic</option>
                  <option value="bulk">Bulk Order Inquiry</option>
                  <option value="pricing">Pricing Query</option>
                  <option value="order">Order Issue</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Message *</label>
                <textarea className="input-field resize-none" rows={5} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} required />
              </div>
              {error && (
                <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">{error}</p>
              )}
              <button type="submit" disabled={sending} className="btn-primary w-full flex items-center justify-center gap-2">
                {sending ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Sending…</> : 'Send Message'}
              </button>
            </form>
          )}
        </div>

        <div className="space-y-6">
          {[
            { icon: '📍', title: 'Address', lines: ['386 Johannes Ramkhoase Street', 'Pretoria Central, Gauteng, 0002'] },
            { icon: '📞', title: 'Phone', lines: ['012 323 1520'] },
            { icon: '📧', title: 'Email', lines: ['info@tonysliquor.co.za'] },
            { icon: '🕐', title: 'Business Hours', lines: ['Mon–Fri: 08:00 – 18:00', 'Saturday: 08:00 – 15:00', 'Sunday: Closed'] },
          ].map(({ icon, title, lines }) => (
            <div key={title} className="flex gap-4">
              <div className="text-3xl">{icon}</div>
              <div>
                <div className="font-display font-semibold text-gray-800 dark:text-white">{title}</div>
                {lines.map((l, i) => <p key={i} className="text-sm text-gray-500 dark:text-gray-400">{l}</p>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Not Found ─────────────────────────────────────────────────────
export function NotFound() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 animate-fade-in">
      <div className="text-center">
        <div className="font-heading text-8xl text-primary/20 dark:text-primary/10 mb-4">404</div>
        <h2 className="font-heading text-3xl text-gray-900 dark:text-white mb-3">Page Not Found</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-8">The page you're looking for doesn't exist.</p>
        <div className="flex gap-3 justify-center">
          <Link to="/" className="btn-primary">Go Home</Link>
          <Link to="/shop" className="btn-outline">Browse Products</Link>
        </div>
      </div>
    </div>
  )
}

// ── Order Detail ──────────────────────────────────────────────────
export function OrderDetail() {
  const { id } = useParams()
  const { isAuthenticated, loading } = useAuth()

  const { data: order, isLoading, error } = useQuery({
    queryKey: ['order', id],
    queryFn: () => orderService.getById(id),
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
  })

  const { data: pubSettings } = useQuery({
    queryKey: ['public-settings'],
    queryFn: () => publicSettingsService.get().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const showOrderStatus = pubSettings?.show_order_status ?? false

  if (loading || isLoading) return <PageSpinner />
  if (!isAuthenticated) return <Navigate to="/login" replace />

  if (error || !order) return (
    <div className="max-w-3xl mx-auto px-4 py-12 text-center animate-fade-in">
      <div className="text-5xl mb-4">📦</div>
      <h3 className="font-heading text-xl mb-3">Order not found</h3>
      <Link to="/orders" className="btn-primary">Back to Orders</Link>
    </div>
  )

  const items = order.items || order.order_items || []
  const address = order.delivery_address || order.shipping_address || null

  const statusColor = (s) => ({
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    shipped: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    delivered: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }[s] || 'bg-gray-100 text-gray-700')

  const STATUS_STEPS = ['pending','processing','shipped','delivered']
  const currentStep = STATUS_STEPS.indexOf(order.status)

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to="/orders" className="text-gray-400 hover:text-gray-600 transition-colors">
          <ChevronRight size={20} className="rotate-180" />
        </Link>
        <div className="flex-1">
          <h1 className="font-heading text-2xl text-gray-900 dark:text-white">Order #{order.id}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Placed {(order.order_date || order.created_at) ? new Date(order.order_date || order.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {showOrderStatus && (
            <span className={`text-xs px-3 py-1 rounded-full font-display font-semibold ${statusColor(order.status)}`}>
              {order.status || 'Pending'}
            </span>
          )}
          <ReorderButton orderId={order.id} />
        </div>
      </div>

      {/* Progress tracker — only shown when order status is visible */}
      {showOrderStatus && order.status !== 'cancelled' && (
        <div className="card p-6 mb-5">
          <h2 className="font-display font-semibold text-sm text-gray-700 dark:text-gray-200 mb-4">Order Progress</h2>
          <div className="flex items-center">
            {STATUS_STEPS.map((step, i) => {
              const done = i <= currentStep
              const labels = { pending: 'Placed', processing: 'Processing', shipped: 'Shipped', delivered: 'Delivered' }
              return (
                <div key={step} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                      done ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                    }`}>
                      {done ? '✓' : i + 1}
                    </div>
                    <span className={`text-xs mt-1 font-display ${done ? 'text-primary dark:text-red-400 font-semibold' : 'text-gray-400'}`}>
                      {labels[step]}
                    </span>
                  </div>
                  {i < STATUS_STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 mb-5 transition-colors ${i < currentStep ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'}`} />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5">
        {/* Items — takes 2 cols */}
        <div className="md:col-span-2 card overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
            <Package size={16} className="text-primary" />
            <span className="font-display font-semibold text-gray-800 dark:text-white text-sm">
              {items.length} Item{items.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {items.map((item, i) => {
              const imgSrc = item.product?.primary_image ? `/MappedData/${item.product.primary_image}` : null
              const name = item.product?.product_name || item.product?.description || item.product_name || `Product #${item.product_id}`
              return (
                <div key={item.id || i} className="flex items-center gap-4 px-5 py-4">
                  <div className="w-14 h-14 rounded-lg bg-gray-50 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                    {imgSrc ? (
                      <img src={imgSrc} alt="" className="w-full h-full object-contain p-1 rounded-lg" />
                    ) : (
                      <span className="text-3xl">🍾</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-semibold text-sm text-gray-900 dark:text-white truncate">{name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Qty: {item.quantity}</p>
                  </div>
                  <span className="font-display font-bold text-sm text-gray-800 dark:text-white">
                    R{parseFloat((item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Right column: summary + address */}
        <div className="space-y-4">
          {/* Summary */}
          <div className="card p-5">
            <h3 className="font-display font-semibold text-sm text-gray-700 dark:text-gray-200 mb-3">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-500">
                <span>Subtotal</span>
                <span>R{parseFloat(order.subtotal || order.total_amount || 0).toFixed(2)}</span>
              </div>
              {order.delivery_fee != null && (
                <div className="flex justify-between text-gray-500">
                  <span>Delivery</span>
                  <span>{parseFloat(order.delivery_fee) === 0 ? 'Free' : `R${parseFloat(order.delivery_fee).toFixed(2)}`}</span>
                </div>
              )}
              <div className="flex justify-between font-display font-bold text-gray-900 dark:text-white border-t border-gray-100 dark:border-gray-700 pt-2">
                <span>Total</span>
                <span className="text-primary dark:text-red-400 text-base">
                  R{parseFloat(order.total_amount || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Address */}
          {address && (
            <div className="card p-5">
              <h3 className="font-display font-semibold text-sm text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-1">
                <MapPin size={14} className="text-primary" /> Delivery Address
              </h3>
              <div className="text-sm text-gray-500 dark:text-gray-400 space-y-0.5">
                {typeof address === 'string' ? (
                  <p className="whitespace-pre-line">{address}</p>
                ) : (
                  <>
                    {address.full_name && <p className="font-medium text-gray-700 dark:text-gray-200">{address.full_name}</p>}
                    {address.address_line1 && <p>{address.address_line1}</p>}
                    {address.address_line2 && <p>{address.address_line2}</p>}
                    {(address.city || address.province) && <p>{[address.city, address.province].filter(Boolean).join(', ')}</p>}
                    {address.postal_code && <p>{address.postal_code}</p>}
                  </>
                )}
              </div>
            </div>
          )}

          {order.notes && (
            <div className="card p-5">
              <h3 className="font-display font-semibold text-sm text-gray-700 dark:text-gray-200 mb-2">Notes</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">{order.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
